import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { COMPANY, SERVICES } from "@/data/site";
import { trackCallConversion } from "@/lib/analytics";
import { CrestIcon } from "@/components/CrestIcon";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services", children: SERVICES },
  { label: "Commercial", to: "/commercial" },
  { label: "Gallery", to: "/gallery" },
  { label: "Reviews", to: "/reviews" },
  { label: "Areas", to: "/areas" },
  { label: "Blog", to: "/blog" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setSvcOpen(false); }, [loc.pathname]);

  const overHero = loc.pathname === "/" && !scrolled && !open;
  const linkColor = overHero ? "text-white/90 hover:text-white" : "text-[var(--ink)] hover:text-[var(--oxblood)]";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${overHero ? "bg-transparent" : "backdrop-blur-xl bg-white/90 border-b border-[var(--border)]"}`}
    >
      <div className="px-6 md:px-12 max-w-7xl mx-auto flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5">
          <CrestIcon className={`w-6 h-7 md:w-7 md:h-8 shrink-0 transition-colors ${overHero ? "text-white/90" : "text-[#A3A3A3]"}`} />
          <span className="flex flex-col leading-none">
            <span className={`font-display text-xl md:text-2xl tracking-[0.15em] transition-colors ${overHero ? "text-white" : "text-[var(--ink)]"}`}>STONECREST</span>
            <span className="text-[10px] tracking-[0.35em] uppercase text-[var(--oxblood)] mt-0.5">Exterior Specialists</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.to} className="relative" onMouseEnter={() => setSvcOpen(true)} onMouseLeave={() => setSvcOpen(false)}>
                <Link to={item.to} data-testid={`nav-${item.label.toLowerCase()}`} className={`flex items-center gap-1 text-sm tracking-wide transition-colors ${linkColor}`}>
                  {item.label} <ChevronDown className="w-3.5 h-3.5" />
                </Link>
                <AnimatePresence>
                  {svcOpen && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                      <div className="bg-white border border-[var(--border)] shadow-xl w-64 py-2">
                        {item.children.map((s) => (
                          <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`nav-service-${s.slug}`} className="block px-5 py-2.5 text-sm text-[var(--ink)] hover:bg-[var(--bone)] hover:text-[var(--oxblood)] transition-colors">
                            {s.name}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link key={item.to} to={item.to} data-testid={`nav-${item.label.toLowerCase()}`} className={`text-sm tracking-wide transition-colors ${linkColor}`}>
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a href={COMPANY.phoneHref} onClick={trackCallConversion} data-testid="header-call" className={`flex items-center gap-2 text-sm whitespace-nowrap transition-colors ${linkColor}`}>
            <Phone className="w-4 h-4" /> {COMPANY.phone}
          </a>
          <Link to="/quote" data-testid="header-quote-btn" className="bg-[var(--oxblood)] text-white px-6 py-3 text-sm tracking-wide whitespace-nowrap rounded-full hover:bg-[var(--oxblood-hover)] transition-colors">
            Get a Free Quote
          </Link>
        </div>

        <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" className={`lg:hidden p-2 ${overHero ? "text-white" : "text-[var(--ink)]"}`} aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="lg:hidden overflow-hidden bg-white border-t border-[var(--border)]">
            <div className="px-6 py-5 flex flex-col gap-1">
              {NAV.map((item) => (
                <Link key={item.to} to={item.to} data-testid={`mobile-nav-${item.label.toLowerCase()}`} className="py-2.5 text-base border-b border-[var(--border)]/60">
                  {item.label}
                </Link>
              ))}
              <div className="flex gap-3 mt-4">
                <a href={COMPANY.phoneHref} onClick={trackCallConversion} className="flex-1 border border-[var(--ink)] text-center py-3 text-sm rounded-full">Call Now</a>
                <Link to="/quote" className="flex-1 bg-[var(--oxblood)] text-white text-center py-3 text-sm rounded-full">Free Quote</Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
